import { Alert, Avatar, Button, Card, ColorPicker, Flex, Form, GetProp, Input, InputNumber, List, Modal, notification, Popconfirm, Radio, Space, Spin, Tag, Tooltip, Tree, Typography, Upload, UploadProps } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ScenariaService from "../services/scenaria-service";
import { ScenarioModel } from "../models/scenario-model";
import { PollutantModel } from "../models/pollutant-model";
import { AnalysisModel } from "../models/analysis-model";
import Icon, { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, CloseOutlined, EditOutlined, InfoCircleOutlined, PlusOutlined, SyncOutlined, UploadOutlined } from "@ant-design/icons";
import { title } from "process";
import { PusherPrivateChannel } from "laravel-echo/dist/channel";
import { debounce, groupBy } from "lodash";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function ScenariaAdminPage() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [paginatedScenaria, setPaginatedScenaria] =useState<ScenarioModel[]>([]);
    const [currentPage, setCurrentPage] = useState<number|undefined>(undefined);
    const [totalScenaria, setTotalScenaria] = useState<number|undefined>(undefined);
    const [scenariaPerPage, setScenariaPerPage] = useState<number>(10);
    const [scenarioToAddOrEdit, setScenarioToAddOrEdit] = useState<ScenarioModel|undefined>(undefined);
    const [pollutantToAddOrEdit, setPollutantToAddOrEdit] = useState<PollutantModel|undefined>(undefined);
    const [analysisToAddOrEdit, setAnalysisToAddOrEdit] = useState<AnalysisModel|undefined>(undefined);
    const [isJsonScaleModalOpen, setIsJsonScaleModalOpen] = useState<boolean>(false);
    const [jsonScaleString, setJsonScaleString] = useState<string|undefined>(undefined);
    const [processingAnalyses, setProcessingAnalyses] = useState<AnalysisModel[]>([]);
    const stateRefProcessingAnalyses = useRef(processingAnalyses);
    
    const navigate = useNavigate();
    const scenariaService = ScenariaService.getInstance();

    let analysisChannel : PusherPrivateChannel = window.Echo.private(`Analysis`);
    let generalErrorChannel : PusherPrivateChannel = window.Echo.private(`GeneralBackgroundError`);

    useEffect(()=> {
        analysisChannel.listen('AnalysisChangedEvent', (e:any) => {
            const scenarioId = e.scenario_id as number;
            const pollutantId = e.pollutant_id as number;
            const analysisId = e.id as number;
            scenariaService.getAnalysis(scenarioId, pollutantId, analysisId).subscribe({
                next:(fullAnalysis) => {
                    const analyses = stateRefProcessingAnalyses.current.map(a => {
                        if (a.id !== fullAnalysis.id) return a;
                        return fullAnalysis;
                    });
                    stateRefProcessingAnalyses.current = analyses;
                    setProcessingAnalyses([...analyses]);
    
    
                    if (fullAnalysis.generate_polygon_contours_batch) {
                        notification.info({message:`Analysis ${fullAnalysis.title} contour generation progress ${fullAnalysis.generate_polygon_contours_batch.progress}%`});
                        return;
                    }
                    if (fullAnalysis.process_dat_file_job) {
                        notification.info({message:`Analysis ${fullAnalysis.title} initial DAT file: ${fullAnalysis.process_dat_file_job.status}`});
                        return;
                    }
                    notification.info({message:`Analysis ${fullAnalysis.title} processing`});
                }
            });
        });

        generalErrorChannel.listen('GeneralBackgroundErrorOccuredEvent', (e:any) => {
            const errorCode = e.error_code;
            if (errorCode === "analysis_job_error") {
                notification.error({message:"Error processing analysis"});
            }
        });
        
        return () => {
            analysisChannel.stopListening('AnalysisChangedEvent');
            generalErrorChannel.stopListening('GeneralBackgroundErrorOccuredEvent');
        }
    },[]);



    useEffect(()=>{
        loadScenaria(1, 10);
        scenariaService.getProcessingAnalysis().subscribe({
            next:(response) => {
                setProcessingAnalyses(response);
                stateRefProcessingAnalyses.current = response;
            }
        });
    },[]);


    const loadScenaria = (page:number, pageSize:number) => {
        setIsLoading(true);
        scenariaService.getScenaria(page, pageSize).subscribe({
            next:(response) => {
                setCurrentPage(response.meta.current_page);
                setScenariaPerPage(response.meta.per_page);
                setPaginatedScenaria(response.data);
                setTotalScenaria(response.meta.total)
                setIsLoading(false);
            },
            error:(error)=> {
                notification.error({message:"Error getting scenaria"});
                setIsLoading(false);
            }
        });
    }

    return(
    <><div style={{display:'flex',flexDirection:'column',height:'100%'}}>
        {(processingAnalyses && processingAnalyses.length>0) && <Alert type="info" description={<>
            <h3 style={{marginTop:0}}>Processing analyses</h3>
            <ol>
            {processingAnalyses.map((p,i)=><li key={i}><Space  direction="horizontal">
                <div>{p.title}</div>
                <div>{p.process_status}</div>
                <div>Initial DAT file status: {p.process_dat_file_job ? p.process_dat_file_job.status : '-'}</div>
                <div>Contour generation progress: {p.generate_polygon_contours_batch ? p.generate_polygon_contours_batch.progress : '-'}</div>
            </Space></li> )}    
            </ol>
        </>}/>}
        

        <div style={{display:'flex',flexDirection:'row',width:'100%'}}>
            <h2 key='title'>Scenaria list</h2>
            <div style={{flex:1}}></div>
            <div key='add-button' style={{alignContent:'center'}}>
                <Button type="primary" size="large" onClick={()=>setScenarioToAddOrEdit({} as ScenarioModel)} icon={<PlusOutlined />}>Add scenario</Button>
            </div>
            
        </div>
        
        <List 
            style={{flex:1,display:'flex',flexDirection:'column',height:`calc(100% - 200px)`}}
            pagination={{
                current:currentPage,
                total:totalScenaria,
                pageSize:scenariaPerPage,
                onChange:(page, pageSize)=> {
                    loadScenaria(page, pageSize);
                }
            }} 
            dataSource={paginatedScenaria}
            renderItem={(item, index) => (
                <List.Item key={index} style={{alignItems:'start'}} extra={[
                    <Button key="1" type="link" icon={<EditOutlined />} onClick={()=>setScenarioToAddOrEdit({...item} as ScenarioModel)}></Button>,
                    <Popconfirm title='Are you sure?' key='deleteButton' onConfirm={()=> {
                        setIsLoading(true);
                        scenariaService.deleteScenario(item.id).subscribe({
                            next:()=>{
                                loadScenaria(1, scenariaPerPage);
                                notification.success({message:"Scenario deleted."})
                            },
                            error:()=>{
                                setIsLoading(false);
                                notification.error({message:"Error deleting scenario"});
                            }
                        });
                    }}><Button type="link" icon={<CloseOutlined />}></Button></Popconfirm>,
                    <Button key='addPollutant' type="link" onClick={()=>setPollutantToAddOrEdit({scenario_id:item.id} as PollutantModel)}>Add pollutant</Button>
                ]}>
                    <List.Item.Meta
                        title={<span style={{marginTop:0}}>{item.title}</span>}
                        description={ <>
                            <Typography.Paragraph style={{whiteSpace:'pre-wrap'}} ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}>{item.description}</Typography.Paragraph>
                            {(!item.pollutants || !item.pollutants.length) ? <div>No pollutants added.</div>: 
                            <div style={{display:'flex', flexDirection:'row', width:'100%', overflowX:'auto', gap:20}}>
                                {item.pollutants.map((p,i)=> {
                                    return <Card 
                                        key={i} 
                                        title={<div style={{display:'flex', flexDirection:'row', width:'100%', gap:10}}>
                                            <div style={{flex:1}}>Pollutant: {p.name} {p.description && <Tooltip title={<div style={{whiteSpace:'pre-wrap'}}>{p.description}</div>}><InfoCircleOutlined style={{marginLeft:10}}/> </Tooltip>}</div>
                                            <Tooltip title='Edit pollutant'><Button icon={<EditOutlined/>} onClick={()=>setPollutantToAddOrEdit({...p})}/></Tooltip>
                                            <Popconfirm title='Are you sure?' key='deletePollutant' onConfirm={(e)=>{
                                                setIsLoading(true);
                                                scenariaService.deletePollutant(item.id, p.id).subscribe({
                                                    next:()=>{
                                                        loadScenaria(1, scenariaPerPage);
                                                        notification.success({message:"Pollutant deleted."})
                                                    },
                                                    error:()=>{
                                                        setIsLoading(false);
                                                        notification.error({message:"Error deleting pollutant"});
                                                    }
                                                });
                                            }} ><Tooltip title='Delete pollutant'><Button icon={<CloseOutlined/>}/></Tooltip></Popconfirm>
                                            <Tooltip title='Add analysis'><Button type='primary' onClick={(e)=>{setAnalysisToAddOrEdit({scenario_id:p.scenario_id, pollutant_id:p.id, time_step_in_hours:3, is_timeseries:true, scale_definition:{scale_items:[{upper_limit:0, hex_color:"#00fe55"}]} } as AnalysisModel);}} icon={<PlusOutlined/>} style={{marginLeft:10}}/></Tooltip>
                                        </div>} 
                                        style={{minWidth:500}}>
                                            
                                            {(!p.analyses || !p.analyses.length) ? <Typography.Text type="secondary">No analyses exists for this pollutant. Add one <a onClick={(e)=>{setAnalysisToAddOrEdit({scenario_id:p.scenario_id, pollutant_id:p.id, time_step_in_hours:3, is_timeseries:true, scale_definition:{scale_items:[{upper_limit:0, hex_color:"#00fe55"}]} } as AnalysisModel);}}>now</a>.</Typography.Text> : 
                                            Object.values(groupBy(p.analyses ?? [], an=>an.time_step_in_hours)).map((groupOfAnalyses, groupIndex)=> <div key={groupIndex}>
                                                <h4 style={{marginBottom:0,marginTop:groupIndex===0?0:10}} key="title">Time step: {groupOfAnalyses[0].time_step_in_hours ? `${groupOfAnalyses[0].time_step_in_hours} hour(s)`: 'aggregate in period'} </h4>
                                                {groupOfAnalyses.map((a,ai)=> {
                                                    const processingAnalysis = stateRefProcessingAnalyses.current.find(pa => pa.id === a.id);
                                                    if (processingAnalysis) a = processingAnalysis;
                                                    return <div key={ai}>
                                                        <a onClick={()=>setAnalysisToAddOrEdit({...a})}>{ai+1}. {a.title}</a> 
                                                        {a.description && <Tooltip title={<div style={{whiteSpace:'pre-wrap'}}>{a.description}</div>}><InfoCircleOutlined style={{marginLeft:10}}/> </Tooltip>}
                                                        {a.process_status === 'error' && <Tag style={{marginLeft:10}} color='error' icon={<CloseCircleOutlined />}>ERROR</Tag>}
                                                        {a.process_status === 'processing' && <Tag style={{marginLeft:10}} color='processing' icon={<SyncOutlined spin />}>PROCESSING</Tag>}
                                                        {a.process_status === 'completed' && <Tag style={{marginLeft:10}} color='success' icon={<CheckCircleOutlined />}>COMPLETED</Tag>}
                                                        {a.process_status === 'not_started' && <Tag style={{marginLeft:10}} color='default' icon={<ClockCircleOutlined />}>NOT STARTED</Tag>}
                                                        <Popconfirm key="deleteAnalysis" title='Are you sure?' onConfirm={()=>{
                                                            setIsLoading(true);
                                                            scenariaService.deleteAnalysis(item.id, p.id, a.id).subscribe({
                                                                next:()=>{
                                                                    loadScenaria(1, scenariaPerPage);
                                                                    notification.success({message:"Analysis deleted."})
                                                                },
                                                                error:()=>{
                                                                    setIsLoading(false);
                                                                    notification.error({message:"Error deleting analysis"});
                                                                }
                                                            });
                                                        }}><Button style={{marginLeft:10}} icon={<CloseOutlined/>} disabled={a.process_status === 'processing' || a.process_status === 'not_started'} size="small"/></Popconfirm>
                                                        {(a.process_status === 'error' || a.process_status === 'completed') && <Button type='link' key='rebuild' onClick={()=>{
                                                            setIsLoading(true);
                                                            scenariaService.rebuildAnalysis(item.id, p.id, a.id).subscribe({
                                                                next:()=>{
                                                                    loadScenaria(1, scenariaPerPage);
                                                                    notification.success({message:"Analysis processing restarted."})
                                                                },
                                                                error:()=>{
                                                                    setIsLoading(false);
                                                                    notification.error({message:"Error restarting analysis processing"});
                                                                }
                                                            });
                                                        }}>Rebuild</Button>}
                                                    </div>;
                                                })}
                                            </div>)
                                            }
                                        
                                    </Card>
                                })}
                            </div>}
                            </>
                        }
                    />
                </List.Item>
            )}>
        </List>
    </div>

    <Modal 
        title={scenarioToAddOrEdit?.id ? 'Edit scenario': 'Add scenario'}
        closable={false}
        open={scenarioToAddOrEdit !== undefined} 
        onCancel={()=>setScenarioToAddOrEdit(undefined)} 
        okButtonProps={{loading:isLoading, disabled:!scenarioToAddOrEdit || !scenarioToAddOrEdit.title}}
        onOk={()=>{
            if (!scenarioToAddOrEdit || !scenarioToAddOrEdit.title) return;
            setIsLoading(true);
            let actionName = "added";
            let action =scenariaService.createScenario(scenarioToAddOrEdit?.title, scenarioToAddOrEdit?.description);
            if (scenarioToAddOrEdit.id) {
                actionName = "updated";
                action = scenariaService.updateScenario(scenarioToAddOrEdit.id, scenarioToAddOrEdit?.title, scenarioToAddOrEdit?.description);
            }
            action.subscribe({
                next:(addedScenario)=> {
                    notification.success({message:`Scenario ${actionName}.`, description:addedScenario.title});
                    setScenarioToAddOrEdit(undefined);
                    loadScenaria(1, scenariaPerPage);
                },
                error:(error) => {
                    setIsLoading(false);
                    notification.error({message:`Scenario not ${actionName}.`});
                }
            });
        }}
    >
        {scenarioToAddOrEdit && <Form layout="vertical">
            <Form.Item
                label="Title"
                required={true}
                validateStatus={!scenarioToAddOrEdit.title ? 'error' : 'success'}
                help={!scenarioToAddOrEdit.title ? 'The field is required':''}
            >
                <Input value={scenarioToAddOrEdit.title} onChange={(e)=>setScenarioToAddOrEdit({...scenarioToAddOrEdit, title:e.target.value})} placeholder="Three day analysis of SO2 from 01/05/2023 to 01/08/2023"/>
            </Form.Item>
            <Form.Item
                label="Description"
            >
                <Input.TextArea rows={5} value={scenarioToAddOrEdit.description} onChange={(e)=>setScenarioToAddOrEdit({...scenarioToAddOrEdit, description:e.target.value})}/>
            </Form.Item>
        </Form>}
    </Modal>



    <Modal 
        title={pollutantToAddOrEdit?.id ? 'Edit pollutant': 'Add pollutant'}
        closable={false}
        open={pollutantToAddOrEdit !== undefined} 
        onCancel={()=>setPollutantToAddOrEdit(undefined)} 
        okButtonProps={{loading:isLoading, disabled:!pollutantToAddOrEdit || !pollutantToAddOrEdit.name}}
        onOk={()=>{
            if (!pollutantToAddOrEdit || !pollutantToAddOrEdit.name) return;
            setIsLoading(true);
            let actionName = "added";
            let action = scenariaService.addPollutant(pollutantToAddOrEdit.scenario_id, pollutantToAddOrEdit.name, pollutantToAddOrEdit.description);
            if (pollutantToAddOrEdit.id) {
                actionName = "updated";
                action = scenariaService.updatePollutant(pollutantToAddOrEdit.scenario_id, pollutantToAddOrEdit.id, pollutantToAddOrEdit.name, pollutantToAddOrEdit.description);
            }
            action.subscribe({
                next:(addedPollutant)=> {
                    notification.success({message:`Pollutant ${actionName}.`, description:addedPollutant.name});
                    setPollutantToAddOrEdit(undefined);
                    loadScenaria(1, scenariaPerPage);
                },
                error:(error) => {
                    setIsLoading(false);
                    notification.error({message:`Pollutant not ${actionName}.`});
                }
            });
        }}
    >
        {pollutantToAddOrEdit && <Form layout="vertical">
            <Form.Item
                label="Name"
                required={true}
                validateStatus={!pollutantToAddOrEdit.name ? 'error' : 'success'}
                help={!pollutantToAddOrEdit.name ? 'The field is required':''}
            >
                <Input value={pollutantToAddOrEdit.name} onChange={(e)=>setPollutantToAddOrEdit({...pollutantToAddOrEdit, name:e.target.value})} placeholder="SO2"/>
            </Form.Item>
            <Form.Item
                label="Description"
            >
                <Input.TextArea rows={5} value={pollutantToAddOrEdit.description} onChange={(e)=>setPollutantToAddOrEdit({...pollutantToAddOrEdit, description:e.target.value})}/>
            </Form.Item>
        </Form>}
    </Modal>


    <Modal 
        title={analysisToAddOrEdit?.id ? 'Edit analysis': 'Add analysis'}
        closable={false}
        open={analysisToAddOrEdit !== undefined} 
        onCancel={()=>setAnalysisToAddOrEdit(undefined)} 
        okButtonProps={{loading:isLoading, disabled:!analysisToAddOrEdit || !analysisToAddOrEdit.title || !analysisToAddOrEdit.units || (analysisToAddOrEdit.is_timeseries === true && !analysisToAddOrEdit.time_step_in_hours) || analysisToAddOrEdit.is_timeseries===undefined || analysisToAddOrEdit.scale_definition === undefined || analysisToAddOrEdit.scale_definition.scale_items ===undefined || analysisToAddOrEdit.scale_definition.scale_items.length ===0 || (!analysisToAddOrEdit.filename && (!analysisToAddOrEdit.filesToSubmit || !analysisToAddOrEdit.filesToSubmit.length))}}
        cancelButtonProps={{disabled:isLoading}}
        onOk={()=>{
            if (!analysisToAddOrEdit || !analysisToAddOrEdit.title) return;
            setIsLoading(true);
            const formData = new FormData();
            formData.append("title",analysisToAddOrEdit.title);
            if (analysisToAddOrEdit.description) formData.append("description",analysisToAddOrEdit.description);
            if (analysisToAddOrEdit.units) formData.append("units", analysisToAddOrEdit.units);
            formData.append("is_timeseries", analysisToAddOrEdit.is_timeseries ? "1":"0");
            if (analysisToAddOrEdit.is_timeseries === true) {
                formData.append("time_step_in_hours", analysisToAddOrEdit.time_step_in_hours!.toFixed(0));
            }
            formData.append("scale_definition", JSON.stringify(analysisToAddOrEdit.scale_definition));
            if (analysisToAddOrEdit.filesToSubmit && analysisToAddOrEdit.filesToSubmit.length) formData.append("submitted_file", analysisToAddOrEdit.filesToSubmit[0] as FileType);
            let actionName = "added";
            let action = scenariaService.addAnalysis(analysisToAddOrEdit.scenario_id, analysisToAddOrEdit.pollutant_id, formData);
            if (analysisToAddOrEdit.id) {
                actionName = "updated";
                action = scenariaService.updateAnalysis(analysisToAddOrEdit.scenario_id, analysisToAddOrEdit.pollutant_id, analysisToAddOrEdit.id, formData);
            }
            action.subscribe({
                next:(addedAnalysis)=> {
                    notification.success({message:`Analysis ${actionName}.`, description:addedAnalysis.title});
                    const pa = [...processingAnalyses];
                    pa.push(addedAnalysis);
                    setProcessingAnalyses(pa);
                    stateRefProcessingAnalyses.current = pa;
                    setAnalysisToAddOrEdit(undefined);
                    loadScenaria(1, scenariaPerPage);
                },
                error:(error) => {
                    setIsLoading(false);
                    notification.error({message:`Analysis not ${actionName}.`});
                }
            });
        }}
    >
        {analysisToAddOrEdit && <Form layout="vertical">
            <Form.Item
                label="Name"
                required={true}
                validateStatus={!analysisToAddOrEdit.title ? 'error' : 'success'}
                help={!analysisToAddOrEdit.title ? 'The field is required':''}
            >
                <Input value={analysisToAddOrEdit.title} onChange={(e)=>setAnalysisToAddOrEdit({...analysisToAddOrEdit, title:e.target.value})} placeholder="3 days, average concentration"/>
            </Form.Item>
            <Form.Item
                label="Description"
            >
                <Input.TextArea rows={5} value={analysisToAddOrEdit.description} onChange={(e)=>setAnalysisToAddOrEdit({...analysisToAddOrEdit, description:e.target.value})}/>
            </Form.Item>
            <Form.Item
                label="Timeseries"
            >
                <Radio.Group value={analysisToAddOrEdit.is_timeseries} onChange={e=>{setAnalysisToAddOrEdit({...analysisToAddOrEdit, is_timeseries:e.target.value, time_step_in_hours:undefined})}}>
                    <Radio key='true' value={true}>Yes</Radio>
                    <Radio key='false' value={false}>No</Radio>
                </Radio.Group>
            </Form.Item>
            {analysisToAddOrEdit.is_timeseries === true && <Form.Item
                label="Time step (hours)"
            >
                <Radio.Group value={analysisToAddOrEdit.time_step_in_hours} onChange={(e)=>setAnalysisToAddOrEdit({...analysisToAddOrEdit, time_step_in_hours:e.target.value})}>
                    <Radio key='1' value={1}>1H</Radio>
                    <Radio key='3' value={3}>3H</Radio>
                    <Radio key='8' value={8}>8H</Radio>
                    <Radio key='24' value={24}>24H</Radio>
                </Radio.Group>
            </Form.Item>}
           
            <Form.Item
                label="Units"
                required={true}
            >
                <Input value={analysisToAddOrEdit.units} onChange={(e)=>setAnalysisToAddOrEdit({...analysisToAddOrEdit, units:e.target.value})} placeholder="µg/m³"/>
            </Form.Item>

            <List header={<><span>Scale steps </span> <Button size='small' shape="circle" type="primary" onClick={()=> {
                const newScaleDefintion = {...analysisToAddOrEdit.scale_definition};
                newScaleDefintion.scale_items.push({hex_color:"#FF0000", upper_limit:newScaleDefintion.scale_items[newScaleDefintion.scale_items.length-1].upper_limit+1});
                setAnalysisToAddOrEdit({...analysisToAddOrEdit, scale_definition: newScaleDefintion});
            }} icon={<PlusOutlined/>}/> <Button type='link' onClick={()=> {
                setJsonScaleString(JSON.stringify(analysisToAddOrEdit!.scale_definition, null, "\t"));
                setIsJsonScaleModalOpen(true); 
            }}>JSON format</Button></>} dataSource={analysisToAddOrEdit.scale_definition.scale_items} renderItem={(item, index)=> 
                <div key={index} style={{display:'flex', flexDirection:'row'}}>
                    <div style={{alignContent:'center',marginRight:10}}>{`Scale step ${index+1}`}</div>
                    <InputNumber value={item.upper_limit} precision={2} onChange={(newMin)=>{
                        const newScaleDefintion = {...analysisToAddOrEdit.scale_definition};
                        newScaleDefintion.scale_items[index] = {...newScaleDefintion.scale_items[index], upper_limit:newMin!}
                        setAnalysisToAddOrEdit({...analysisToAddOrEdit, scale_definition: newScaleDefintion});
                    }} placeholder={index === 0 ? "0" : (analysisToAddOrEdit.scale_definition.scale_items[index-1].upper_limit+1).toFixed(2)}/>
                    <ColorPicker value={item.hex_color} showText onChange={(value,css)=> {
                        const newScaleDefintion = {...analysisToAddOrEdit.scale_definition};
                        newScaleDefintion.scale_items[index] = {...newScaleDefintion.scale_items[index], hex_color:value.toHexString()}
                        setAnalysisToAddOrEdit({...analysisToAddOrEdit, scale_definition: newScaleDefintion});
                    }} />
                    <Button type='link' onClick={()=> {
                        if (analysisToAddOrEdit.scale_definition.scale_items.length === 1) return;
                        const newScaleDefintion = {...analysisToAddOrEdit.scale_definition};
                        newScaleDefintion.scale_items.splice(index, 1);
                        setAnalysisToAddOrEdit({...analysisToAddOrEdit, scale_definition: newScaleDefintion});
                    }}>Delete</Button>
                </div>
            }>

            </List>
            <Form.Item
                label="DAT file"
                validateStatus={(!analysisToAddOrEdit.filename && (!analysisToAddOrEdit.filesToSubmit || !analysisToAddOrEdit.filesToSubmit.length)) ? 'error':'success'}
                help={(!analysisToAddOrEdit.filename && (!analysisToAddOrEdit.filesToSubmit || !analysisToAddOrEdit.filesToSubmit.length)) ? 'The file is required':''}
            >
                <Upload multiple={false} maxCount={1} fileList={analysisToAddOrEdit.filesToSubmit} onRemove={(file)=>setAnalysisToAddOrEdit({...analysisToAddOrEdit, filesToSubmit:[]})} beforeUpload={(file)=>{setAnalysisToAddOrEdit({...analysisToAddOrEdit, filesToSubmit:[file]}); return false;}}>
                    <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
                {analysisToAddOrEdit.filename && <Alert style={{marginTop:10}} type="info" description={<span>Existing file: <span style={{textDecoration:analysisToAddOrEdit.filesToSubmit && analysisToAddOrEdit.filesToSubmit.length ? 'line-through':'unset'}}>{analysisToAddOrEdit.filename}</span></span>}/>}
            </Form.Item>
        </Form>}
    </Modal>

    {analysisToAddOrEdit && <Modal open={isJsonScaleModalOpen} closable={false} onOk={()=>{
        const newScale = JSON.parse(jsonScaleString!);
        setAnalysisToAddOrEdit({...analysisToAddOrEdit!, scale_definition: newScale});
        setIsJsonScaleModalOpen(false);
        setJsonScaleString(undefined);
    }} onCancel={()=> {
        setIsJsonScaleModalOpen(false);
        setJsonScaleString(undefined);
    }}>
        <Input.TextArea rows={10} value={jsonScaleString} onChange={(e)=>setJsonScaleString(e.target.value)}/>
    </Modal>}
    
    </>);
}

export default ScenariaAdminPage;